import debug from "../../../utils/debug"; // Import debug utility

// Export South African provinces for location-based filtering
export const provinces = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape'
];

// Musician Genre List (for Solo, Duo, Band, Orchestra, Instrumental, Choir)
const musicianGenres = [
  'Pop', 'Rock', 'Metal', 'Blues', 'R&B/Soul', 'Cover/Top 40', 'Hip Hop & Rap', 
  'Jazz', 'Country', 'Disco/Funk', 'Folk/Acoustic', 'Reggae', 'Gospel', 
  'Classical', 'World/Native'
];

// DJ Genre List (for DJ & Electronic)
const djGenres = [
  'RnB/Soul', 'Top 40', 'Hip Hop & Rap', 'Disco', 'Funk', 'Afrohouse', 
  'House', 'Techno', 'Drum & Bass', 'Amapiano', 'Trance', 'Reggae/Dancehall', 
  'Latin', 'World/Native'
];

/**
 * NEW TALENT CATEGORIES STRUCTURE
 * 8 Main Categories with hierarchical sub-categories
 * Only "Musician & DJ" has additional Style/Genre level
 * 
 * This structure replaces the old complex hierarchy while maintaining
 * all existing functionality through the helper functions below.
 */
export const talentCategories = {
  "Musician & DJ": {
    levels: ['Performance Type', 'Genre'], // Removed 'Style' level
    data: {
      performanceType: ['Solo', 'Duo', 'Band', 'Orchestra', 'Instrumental', 'Choir', 'DJ & Electronic'],
      // Direct mapping to genres - no intermediate Style level
      Genre: {
        // For musician sub-categories - direct to genres
        'Solo': musicianGenres,
        'Duo': musicianGenres,
        'Band': musicianGenres,           // ← Direct mapping, no band types
        'Orchestra': musicianGenres,
        'Instrumental': musicianGenres,
        'Choir': musicianGenres,
        
        // For DJ & Electronic sub-category
        'DJ & Electronic': djGenres
      }
    }
  },

  "Comedy": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Stand-up Comedy',
        'Improv Comedy',
        'Character Comedy',
        'Musical Comedy',
        'Sketch Comedy',
        'Alternative Comedy',
        'Roast Comedy'
      ],
      Style: {
        'Stand-up Comedy': [
          'Family-Friendly',
          'Adult/Mature',
          'Corporate',
          'Observational',
          'Political/Satirical',
          'Cultural',
          'Alternative',
          'One-Liner',
          'Storytelling',
          'Dark Humor'
        ],
        'Improv Comedy': [
          'Short-Form/Games',
          'Long-Form/Theatrical',
          'Musical Improv',
          'Character-Based',
          'Interactive/Audience'
        ],
        'Character Comedy': [
          'Impressions',
          'Original Characters',
          'Sketch Performance',
          'One-Person Shows',
          'Physical Comedy'
        ],
        'Musical Comedy': [
          'Parody Songs',
          'Original Comedy Music',
          'Musical Impressions',
          'Comedy Cabaret',
          'Comedy Band'
        ],
        'Sketch Comedy': [
          'Live Sketch',
          'Character Sketch',
          'Political/Satirical',
          'Physical Comedy',
          'Variety Show'
        ],
        'Alternative Comedy': [
          'Experimental',
          'Surreal',
          'Performance Art',
          'Meta Comedy',
          'Avant-Garde'
        ],
        'Roast Comedy': [
          'Professional Roast',
          'Battle Comedy',
          'Corporate Roast',
          'Celebrity Style',
          'Competition'
        ]
      }
    }
  },

  "MCs & Public Speaking": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Corporate Speaker',
        'Event Host',
        'Master of Ceremonies',
        'Show Host',
        'Conference Host',
        'Wedding Host',
        'Sports Announcer'
      ],
      Style: {
        'Corporate Speaker': [
          'Keynote Speaking',
          'Business Presentations',
          'Training/Workshop',
          'Panel Moderation',
          'Award Ceremonies'
        ],
        'Event Host': [
          'Festival Host',
          'Concert Host',
          'Gala Events',
          'Fashion Shows',
          'Product Launches'
        ],
        'Master of Ceremonies': [
          'Wedding MC',
          'Cultural Events',
          'Award Shows',
          'Charity Events',
          'Private Functions'
        ],
        'Show Host': [
          'Game Shows',
          'Talk Shows',
          'Variety Shows',
          'Quiz Shows',
          'Talent Shows'
        ],
        'Conference Host': [
          'Business Conferences',
          'Tech Events',
          'Academic Conferences',
          'Industry Summits',
          'Virtual Conferences'
        ],
        'Wedding Host': [
          'Traditional Weddings',
          'Modern Celebrations',
          'Cultural Ceremonies',
          'Destination Weddings',
          'Same-Sex Weddings'
        ],
        'Sports Announcer': [
          'Live Sports Events',
          'Tournament Hosting',
          'Stadium Announcing',
          'eSports Events',
          'Sports Awards'
        ]
      }
    }
  },

  "Spectacle & Wonder": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Actor',
        'Dancer',
        'Circus Performer',
        'Magician',
        'Performance Artist',
        'Physical Theater Artist'
      ],
      Style: {
        'Actor': [
          'Theater/Stage',
          'Film/TV',
          'Voice Acting',
          'Musical Theater',
          'Character Acting',
          'Physical Theater',
          'Improvisation',
          'Stunt Performance',
          'Puppetry'
        ],
        'Dancer': [
          'Contemporary/Modern',
          'Classical Ballet',
          'Urban/Street',
          'Jazz/Musical Theater',
          'Ballroom/Latin',
          'Cultural/Traditional',
          'Commercial/Entertainment',
          'Experimental/Contemporary'
        ],
        'Circus Performer': [
          'Acrobatics/Tumbling',
          'Aerial Arts',
          'Fire Performance',
          'Object Manipulation',
          'Balance Acts',
          'Clowning/Character',
          'Specialty Acts'
        ],
        'Magician': [
          'Stage Illusions',
          'Close-up Magic',
          'Mentalism',
          'Comedy Magic',
          'Children\'s Entertainment',
          'Street Magic',
          'Specialty Magic'
        ],
        'Performance Artist': [
          'Interactive Performance',
          'Multimedia Performance',
          'Experimental',
          'Site-Specific',
          'Durational Performance',
          'Conceptual Art',
          'Live Art'
        ],
        'Physical Theater Artist': [
          'Mime/Movement',
          'Dance Theater',
          'Mask Performance',
          'Object Theater',
          'Visual Theater',
          'Movement Storytelling'
        ]
      }
    }
  },

  "Fitness & Sports": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Yoga Instructor',
        'Fitness Performer',
        'Martial Artist',
        'Movement Artist',
        'Wellness Practitioner'
      ],
      Style: {
        'Yoga Instructor': [
          'Hatha Yoga',
          'Vinyasa Yoga',
          'Ashtanga Yoga',
          'Iyengar Yoga',
          'Bikram Yoga',
          'Kundalini Yoga',
          'Restorative Yoga',
          'Aerial Yoga',
          'Power Yoga',
          'Acro Yoga'
        ],
        'Fitness Performer': [
          'Dance Fitness',
          'Strength Training',
          'Calisthenics',
          'Functional Fitness',
          'HIIT',
          'Parkour',
          'Zumba',
          'Acrobatic Fitness'
        ],
        'Martial Artist': [
          'Karate',
          'Kung Fu',
          'Taekwondo',
          'Judo',
          'Muay Thai',
          'Brazilian Jiu-Jitsu',
          'Capoeira',
          'Krav Maga',
          'Mixed Martial Arts'
        ],
        'Movement Artist': [
          'Contemporary Dance',
          'Contact Improvisation',
          'Ecstatic Dance',
          'Lyrical Movement',
          'Animal Flow',
          'Physical Storytelling',
          'Dance Theater'
        ],
        'Wellness Practitioner': [
          'Meditation Guide',
          'Breathwork',
          'Sound Healing',
          'Qigong',
          'Yoga Nidra',
          'Holistic Healing',
          'Energy Work'
        ]
      }
    }
  },

  "Dance & Rhythm": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Live Visual Artist',
        'Portrait Artist',
        'Photographer',
        'Videographer',
        'Digital Artist',
        'Installation Artist'
      ],
      Style: {
        'Live Visual Artist': [
          'Speed Painting',
          'Body Art',
          'Sand Art',
          'Light Art',
          'Street Art/Graffiti',
          'Live Sculpture',
          'Performance Drawing'
        ],
        'Portrait Artist': [
          'Traditional Portraiture',
          'Digital Portraits',
          'Caricature',
          'Speed Portraits',
          'Silhouette Cutting',
          'Fashion Illustration'
        ],
        'Photographer': [
          'Event Photography',
          'Portrait Photography',
          'Fashion/Editorial',
          'Commercial/Product',
          'Documentary',
          'Fine Art Photography',
          'Specialty Photography'
        ],
        'Videographer': [
          'Event Videography',
          'Commercial Production',
          'Documentary',
          'Live Streaming',
          'Music Videos',
          'Aerial/Drone',
          'Specialty Video'
        ],
        'Digital Artist': [
          'Projection Mapping',
          'Interactive Digital',
          'Motion Graphics',
          'VR/AR Experience',
          'Digital Installation',
          'Live Digital Art'
        ],
        'Installation Artist': [
          'Interactive Installation',
          'Multimedia Installation',
          'Light Installation',
          'Sound Installation',
          'Environmental Art',
          'Site-Specific Art'
        ]
      }
    }
  },

  "Acting & Spoken Word": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Game Master',
        'Experience Designer',
        'Interactive Performer',
        'Mystic Entertainer',
        'Tech Entertainment'
      ],
      Style: {
        'Game Master': [
          'Mystery Games',
          'Escape Experiences',
          'Adventure Games',
          'Quiz/Trivia',
          'Team Building',
          'Role-Playing Games'
        ],
        'Experience Designer': [
          'Immersive Theater',
          'Interactive Stories',
          'Adventure Experiences',
          'Site-Specific Events',
          'Virtual Reality',
          'Mixed Reality'
        ],
        'Interactive Performer': [
          'Audience Participation',
          'Interactive Comedy',
          'Participatory Art',
          'Community Creation',
          'Interactive Music',
          'Live Gaming'
        ],
        'Mystic Entertainer': [
          'Tarot Reading',
          'Palm Reading',
          'Astrology',
          'Psychic Entertainment',
          'Fortune Telling',
          'Spiritual Readings'
        ],
        'Tech Entertainment': [
          'AR Experiences',
          'Interactive Projection',
          'Motion Gaming',
          'Digital Installation',
          'Holographic Display',
          'Tech-Based Games'
        ]
      }
    }
  },

  "Imagery & Visual Arts": {
    levels: ['Performance Type', 'Style'],
    data: {
      performanceType: [
        'Educational Entertainer',
        'Character Performer',
        'Interactive Artist',
        'Children\'s Performer',
        'Kids Musical Act'
      ],
      Style: {
        'Educational Entertainer': [
          'Science Shows',
          'Nature/Wildlife',
          'History/Culture',
          'STEM Activities',
          'Art Education',
          'Interactive Learning',
          'Environmental Education'
        ],
        'Character Performer': [
          'Princess/Prince',
          'Superhero',
          'Cartoon Character',
          'Mascot',
          'Fantasy Character',
          'Storybook Character',
          'Themed Character'
        ],
        'Interactive Artist': [
          'Face Painting',
          'Balloon Art',
          'Games/Activities',
          'Arts & Crafts',
          'Interactive Stories',
          'Party Activities',
          'Creative Workshops'
        ],
        'Children\'s Performer': [
          'Magic Shows',
          'Puppet Shows',
          'Clown Performance',
          'Circus Skills',
          'Variety Acts',
          'Comedy Shows',
          'Specialty Acts'
        ],
        'Kids Musical Act': [
          'Children\'s Music',
          'Interactive Songs',
          'Musical Stories',
          'Music & Movement',
          'Sing-Alongs',
          'Dance Activities',
          'Musical Games'
        ]
      }
    }
  }
};

/**
 * EQUIPMENT LISTS FOR TECHNICAL RIDERS
 * Three different equipment lists mapped to category groups
 */

// Equipment List 1 - Live Music/Bands (18 items)
export const equipmentList1 = [
  { id: 'vocalMic', name: 'Vocal Mic', icon: '🎤', category: 'Microphones' },
  { id: 'instrumentMic', name: 'Instrument Mic', icon: '🎙️', category: 'Microphones' },
  { id: 'clipOnInstrumentMic', name: 'Clip on Instrument Mic', icon: '📎', category: 'Microphones' },
  { id: 'clipOnVocalMic', name: 'Clip on Vocal Mic', icon: '📎', category: 'Microphones' },
  { id: 'diBoxMono', name: 'D.I. Box Mono', icon: '📦', category: 'D.I. Boxes' },
  { id: 'diBoxStereo', name: 'D.I. Box Stereo', icon: '📦', category: 'D.I. Boxes' },
  { id: 'guitarAmp', name: 'Guitar Amp', icon: '🎸', category: 'Amplifiers' },
  { id: 'bassAmp', name: 'Bass Amp', icon: '🎸', category: 'Amplifiers' },
  { id: 'drumKit', name: 'Drum Kit', icon: '🥁', category: 'Instruments' },
  { id: 'drumMics', name: 'Drum Mics', icon: '🥁', category: 'Microphones' },
  { id: 'djDecks', name: 'DJ Decks', icon: '💿', category: 'Decks' },
  { id: 'inEarMonitors', name: 'In-Ear Monitors', icon: '🎧', category: 'Monitors' },
  { id: 'floorMonitor', name: 'Floor Monitor', icon: '📻', category: 'Monitors' },
  { id: 'subMonitor', name: 'Sub Monitor', icon: '📻', category: 'Monitors' },
  { id: 'videoInput', name: 'Video Input', icon: '📹', category: 'Video' },
  { id: 'videoMonitor', name: 'Video Monitor', icon: '📺', category: 'Video' },
  { id: 'keyboard', name: 'Keyboard', icon: '🎹', category: 'Instruments' },
  { id: 'piano', name: 'Piano', icon: '🎹', category: 'Instruments' }
];

// Equipment List 2 - Speaking/Dance (11 items)
export const equipmentList2 = [
  { id: 'vocalMic', name: 'Vocal Mic', icon: '🎤', category: 'Microphones' },
  { id: 'instrumentMic', name: 'Instrument Mic', icon: '🎙️', category: 'Microphones' },
  { id: 'clipOnInstrumentMic', name: 'Clip on Instrument Mic', icon: '📎', category: 'Microphones' },
  { id: 'clipOnVocalMic', name: 'Clip on Vocal Mic', icon: '📎', category: 'Microphones' },
  { id: 'keyboard', name: 'Keyboard', icon: '🎹', category: 'Instruments' },
  { id: 'piano', name: 'Piano', icon: '🎹', category: 'Instruments' },
  { id: 'djDecks', name: 'DJ Decks', icon: '💿', category: 'Decks' },
  { id: 'inEarMonitors', name: 'In-Ear Monitors', icon: '🎧', category: 'Monitors' },
  { id: 'floorMonitor', name: 'Floor Monitor', icon: '📻', category: 'Monitors' },
  { id: 'videoInput', name: 'Video Input', icon: '📹', category: 'Video' },
  { id: 'videoMonitor', name: 'Video Monitor', icon: '📺', category: 'Video' }
];

// Equipment List 3 - Specialty Acts (11 items - same as List 2 for now)
export const equipmentList3 = [
  { id: 'vocalMic', name: 'Vocal Mic', icon: '🎤', category: 'Microphones' },
  { id: 'instrumentMic', name: 'Instrument Mic', icon: '🎙️', category: 'Microphones' },
  { id: 'clipOnInstrumentMic', name: 'Clip on Instrument Mic', icon: '📎', category: 'Microphones' },
  { id: 'clipOnVocalMic', name: 'Clip on Vocal Mic', icon: '📎', category: 'Microphones' },
  { id: 'keyboard', name: 'Keyboard', icon: '🎹', category: 'Instruments' },
  { id: 'piano', name: 'Piano', icon: '🎹', category: 'Instruments' },
  { id: 'djDecks', name: 'DJ Decks', icon: '💿', category: 'Decks' },
  { id: 'inEarMonitors', name: 'In-Ear Monitors', icon: '🎧', category: 'Monitors' },
  { id: 'floorMonitor', name: 'Floor Monitor', icon: '📻', category: 'Monitors' },
  { id: 'videoInput', name: 'Video Input', icon: '📹', category: 'Video' },
  { id: 'videoMonitor', name: 'Video Monitor', icon: '📺', category: 'Video' }
];

/**
 * EQUIPMENT LIST MAPPING TO CATEGORIES
 */
export const categoryToEquipmentMapping = {
  "Musician & DJ": equipmentList1,
  "Comedy": equipmentList2,
  "MCs & Public Speaking": equipmentList2,
  "Dance & Rhythm": equipmentList2,
  "Spectacle & Wonder": equipmentList3,
  "Fitness & Sports": equipmentList3,
  "Acting & Spoken Word": equipmentList3,
  "Imagery & Visual Arts": equipmentList3
};

/**
 * Retrieves all available talent categories.
 * @returns {string[]} Array of category names.
 */
export const getCategories = () => {
  debug.log('talentCategories', 'Getting all talent categories');
  return Object.keys(talentCategories);
};

/**
 * Retrieves performance types for a specific talent category.
 * @param {string} talentCategory - The talent category (e.g., 'Musician & DJ').
 * @returns {string[]} Array of performance types or empty array if invalid.
 */
export const getPerformanceTypes = (talentCategory) => {
  if (!talentCategory || !talentCategories[talentCategory]) {
    debug.warn('talentCategories', 'Invalid talent category provided:', talentCategory);
    return [];
  }
  debug.log('talentCategories', `Getting performance types for category: ${talentCategory}`);
  return talentCategories[talentCategory].data.performanceType || [];
};

/**
 * Retrieves styles for a specific talent category and performance type.
 * @param {string} talentCategory - The talent category (e.g., 'Musician & DJ').
 * @param {string} performanceType - The performance type (e.g., 'Solo').
 * @returns {string[]} Array of styles or empty array if invalid.
 */
export const getStyles = (talentCategory, performanceType) => {
  if (!talentCategory || !performanceType || !talentCategories[talentCategory]) {
    debug.warn('talentCategories', 'Invalid parameters for getStyles:', { talentCategory, performanceType });
    return [];
  }
  debug.log('talentCategories', `Getting styles for ${talentCategory} - ${performanceType}`);
  return talentCategories[talentCategory].data.Style?.[performanceType] || [];
};

/**
 * Retrieves genres for a specific talent category, performance type, and style.
 * For "Musician & DJ": Returns musician genres for most styles, DJ genres for DJ styles.
 * For other categories: Returns empty array as they don't have genres.
 * @param {string} talentCategory - The talent category (e.g., 'Musician & DJ').
 * @param {string} performanceType - The performance type (e.g., 'Solo').
 * @param {string} style - The style (e.g., 'Vocalist').
 * @returns {string[]} Array of genres or empty array if invalid/not applicable.
 */
export const getGenres = (talentCategory, performanceType, style) => {
  if (!talentCategory || !talentCategories[talentCategory]) {
    debug.warn('talentCategories', 'Invalid talent category for getGenres:', talentCategory);
    return [];
  }

  debug.log('talentCategories', `Getting genres for ${talentCategory} - ${performanceType} - ${style}`);
  
  const categoryData = talentCategories[talentCategory].data;
  
  // Special handling for "Musician & DJ" category
  if (talentCategory === "Musician & DJ") {
    if (categoryData.Genre && categoryData.Genre[style]) {
      return categoryData.Genre[style];
    }
    
    // Fallback: if no specific genre mapping found, return appropriate genre list based on performance type
    if (performanceType === 'DJ & Electronic') {
      return djGenres;
    } else {
      return musicianGenres;
    }
  }
  
  // Other categories don't have genres in the new structure
  debug.log('talentCategories', `No genres available for category: ${talentCategory}`);
  return [];
};

/**
 * Check if a category has style/genre options.
 * In the new structure, only "Musician & DJ" has genres.
 * @param {string} talentCategory - The talent category.
 * @returns {boolean} True if category has genre options.
 */
export const hasStyleGenre = (talentCategory) => {
  return talentCategory === "Musician & DJ";
};

/**
 * Get the appropriate equipment list for a talent category.
 * @param {string} talentCategory - The talent category.
 * @returns {Array} Equipment list array.
 */
export const getEquipmentList = (talentCategory) => {
  if (!talentCategory || !categoryToEquipmentMapping[talentCategory]) {
    debug.warn('talentCategories', 'Invalid category for equipment list, using default:', talentCategory);
    return equipmentList1; // Default fallback
  }
  
  debug.log('talentCategories', `Getting equipment list for category: ${talentCategory}`);
  return categoryToEquipmentMapping[talentCategory];
};

/**
 * Get equipment list title based on talent category.
 * @param {string} talentCategory - The talent category.
 * @returns {string} Equipment list title.
 */
export const getEquipmentListTitle = (talentCategory) => {
  const titles = {
    "Musician & DJ": "Musician & DJ Equipment List",
    "Comedy": "Speaking & Performance Equipment List",
    "MCs & Public Speaking": "Speaking & Performance Equipment List",
    "Dance & Rhythm": "Dance & Performance Equipment List",
    "Spectacle & Wonder": "Specialty Performance Equipment List",
    "Fitness & Sports": "Fitness & Sports Equipment List",
    "Acting & Spoken Word": "Acting & Performance Equipment List",
    "Imagery & Visual Arts": "Visual Arts Equipment List"
  };
  
  return titles[talentCategory] || "Performance Equipment List";
};

/**
 * Validates if a combination of category, performance type, style, and genre is valid.
 * Accounts for style and genre being stored as arrays in the TalentBlueprint schema.
 * @param {string} talentCategory - The talent category.
 * @param {string} performanceType - The performance type.
 * @param {string|string[]} style - The style or array of styles.
 * @param {string|string[]} genre - The genre or array of genres (optional).
 * @returns {boolean} True if valid, false otherwise.
 */
export const isValidCombination = (talentCategory, performanceType, style, genre) => {
  try {
    if (!talentCategory || !talentCategories[talentCategory]) {
      debug.warn('talentCategories', 'Invalid talent category:', talentCategory);
      return false;
    }
    
    if (!getPerformanceTypes(talentCategory).includes(performanceType)) {
      debug.warn('talentCategories', `Invalid performance type '${performanceType}' for category '${talentCategory}'`);
      return false;
    }
    
    const validStyles = getStyles(talentCategory, performanceType);
    
    // Handle style as a string or array
    const styleArray = Array.isArray(style) ? style : [style];
    const isValidStyle = styleArray.some(s => validStyles.includes(s));
    
    if (!isValidStyle) {
      debug.warn('talentCategories', `Invalid style '${style}' for performance type '${performanceType}'`);
      return false;
    }
    
    // Handle genre validation if provided
    if (genre) {
      const genreArray = Array.isArray(genre) ? genre : [genre];
      const validGenres = styleArray.flatMap(s => getGenres(talentCategory, performanceType, s));
      
      const isValidGenre = genreArray.some(g => validGenres.includes(g));
      if (!isValidGenre) {
        debug.warn('talentCategories', `Invalid genre '${genre}' for style '${style}'`);
        return false;
      }
    }
    
    debug.log('talentCategories', 'Valid combination confirmed:', { talentCategory, performanceType, style, genre });
    return true;
  } catch (error) {
    debug.error('talentCategories', 'Validation error:', error);
    console.warn('Validation warning:', error.message);
    return false;
  }
};

/**
 * Retrieves all valid styles across all performance types for a category.
 * Useful for filtering without requiring performance type selection first.
 * @param {string} talentCategory - The talent category (e.g., 'Musician & DJ')
 * @returns {string[]} Array of all styles for the category
 */
export const getAllStylesForCategory = (talentCategory) => {
  if (!talentCategory || !talentCategories[talentCategory]) {
    debug.warn('talentCategories', 'Invalid talent category for getAllStylesForCategory:', talentCategory);
    return [];
  }
  
  debug.log('talentCategories', `Getting all styles for category: ${talentCategory}`);
  
  const categoryData = talentCategories[talentCategory].data;
  const performanceTypes = categoryData.performanceType || [];
  
  // Get all styles from all performance types
  const allStyles = performanceTypes.flatMap(type => 
    categoryData.Style?.[type] || []
  );
  
  // Return unique styles
  return [...new Set(allStyles)];
};

/**
 * Retrieves all valid genres across all styles for a category.
 * For "Musician & DJ": Returns both musician and DJ genres.
 * For other categories: Returns empty array as they don't have genres.
 * @param {string} talentCategory - The talent category (e.g., 'Musician & DJ')
 * @returns {string[]} Array of all genres for the category
 */
export const getAllGenresForCategory = (talentCategory) => {
  if (!talentCategory || !talentCategories[talentCategory]) {
    debug.warn('talentCategories', 'Invalid talent category for getAllGenresForCategory:', talentCategory);
    return [];
  }
  
  debug.log('talentCategories', `Getting all genres for category: ${talentCategory}`);
  
  // Only "Musician & DJ" has genres in the new structure
  if (talentCategory === "Musician & DJ") {
    // Combine both musician and DJ genres, removing duplicates
    const allGenres = new Set([...musicianGenres, ...djGenres]);
    return Array.from(allGenres);
  }
  
  // Other categories don't have genres
  debug.log('talentCategories', `No genres available for category: ${talentCategory}`);
  return [];
};

/**
 * NEW FUNCTIONS FOR THE 8-CATEGORY STRUCTURE
 * These functions handle the specific logic for the new category system
 */

/**
 * Get sub-categories for a specific talent category (new structure).
 * Maps to performanceType for backward compatibility.
 * @param {string} talentCategory - The talent category.
 * @returns {string[]} Array of sub-categories.
 */
export const getSubCategories = (talentCategory) => {
  return getPerformanceTypes(talentCategory);
};

/**
 * Get style/genre options for "Musician & DJ" category based on sub-category.
 * For DJ & Electronic: returns DJ genres
 * For all other sub-categories: returns musician genres
 * @param {string} talentCategory - The talent category.
 * @param {string} subCategory - The sub-category (performance type).
 * @returns {string[]} Array of style/genre options.
 */
export const getStyleGenreOptions = (talentCategory, subCategory) => {
  if (talentCategory !== "Musician & DJ") {
    debug.log('talentCategories', `No style/genre options for category: ${talentCategory}`);
    return [];
  }
  
  if (!subCategory) {
    debug.warn('talentCategories', 'No sub-category provided for getStyleGenreOptions');
    return [];
  }
  
  debug.log('talentCategories', `Getting style/genre options for ${talentCategory} - ${subCategory}`);
  
  // Direct mapping from sub-category to genres
  const categoryData = talentCategories[talentCategory].data;
  return categoryData.Genre?.[subCategory] || [];
};

/**
 * Check if a category has sub-categories only (no additional style/genre level).
 * @param {string} talentCategory - The talent category.
 * @returns {boolean} True if category has only sub-categories.
 */
export const hasOnlySubCategories = (talentCategory) => {
  const categoriesWithOnlySubCategories = [
    "Comedy", 
    "MCs & Public Speaking", 
    "Spectacle & Wonder", 
    "Fitness & Sports", 
    "Dance & Rhythm", 
    "Acting & Spoken Word", 
    "Imagery & Visual Arts"
  ];
  
  return categoriesWithOnlySubCategories.includes(talentCategory);
};

/**
 * Check if a category has the full 3-level hierarchy (Category → Sub-Category → Style/Genre).
 * @param {string} talentCategory - The talent category.
 * @returns {boolean} True if category has 3-level hierarchy.
 */
export const hasThreeLevelHierarchy = (talentCategory) => {
  return talentCategory === "Musician & DJ";
};

/**
 * Get the label for the third level (Style/Genre) based on sub-category.
 * @param {string} talentCategory - The talent category.
 * @param {string} subCategory - The sub-category.
 * @returns {string} Label for the third level.
 */
export const getThirdLevelLabel = (talentCategory, subCategory) => {
  if (talentCategory !== "Musician & DJ") {
    return "";
  }
  
  if (subCategory === 'DJ & Electronic') {
    return "DJ Genres";
  } else {
    return "Musician Genres";
  }
};

/**
 * BACKWARD COMPATIBILITY FUNCTIONS
 * These maintain compatibility with existing code while transitioning to new structure
 */

/**
 * Legacy function: Maps old category structure to new.
 * @deprecated Use getSubCategories instead
 */
export const getPerformanceTypesLegacy = getPerformanceTypes;

/**
 * Legacy function: Maps old style structure to new.
 * @deprecated Use getStyleGenreOptions for "Musician & DJ" or getStyles for other categories
 */
export const getStylesLegacy = getStyles;

/**
 * Legacy function: Maps old genre structure to new.
 * @deprecated Use getStyleGenreOptions for "Musician & DJ"
 */
export const getGenresLegacy = getGenres;

/**
 * UTILITY FUNCTIONS FOR FORM HANDLING
 */

/**
 * Get the maximum number of selections allowed for style/genre.
 * @returns {number} Maximum selections allowed (3).
 */
export const getMaxStyleGenreSelections = () => {
  return 3;
};

/**
 * Validate style/genre selection count.
 * @param {string[]} selections - Array of selected style/genres.
 * @returns {boolean} True if selection count is valid.
 */
export const isValidStyleGenreSelectionCount = (selections) => {
  if (!Array.isArray(selections)) {
    return false;
  }
  return selections.length <= getMaxStyleGenreSelections();
};

/**
 * Check if more style/genre selections can be made.
 * @param {string[]} currentSelections - Array of currently selected style/genres.
 * @returns {boolean} True if more selections can be made.
 */
export const canAddMoreStyleGenreSelections = (currentSelections) => {
  if (!Array.isArray(currentSelections)) {
    return true;
  }
  return currentSelections.length < getMaxStyleGenreSelections();
};

/**
 * EQUIPMENT UTILITY FUNCTIONS
 */

/**
 * Group equipment items by category for display.
 * @param {Array} equipmentList - Equipment list array.
 * @returns {Object} Equipment items grouped by category.
 */
export const groupEquipmentByCategory = (equipmentList) => {
  const grouped = {};
  
  if (!Array.isArray(equipmentList)) {
    debug.warn('talentCategories', 'Invalid equipment list provided to groupEquipmentByCategory');
    return {};
  }
  
  equipmentList.forEach(item => {
    if (!grouped[item.category]) {
      grouped[item.category] = [];
    }
    grouped[item.category].push(item);
  });
  
  debug.log('talentCategories', 'Equipment grouped by category', { 
    categories: Object.keys(grouped).length,
    totalItems: equipmentList.length 
  });
  
  return grouped;
};

/**
 * Get equipment item by ID from any equipment list.
 * @param {string} itemId - Equipment item ID.
 * @returns {Object|null} Equipment item or null if not found.
 */
export const getEquipmentItemById = (itemId) => {
  const allEquipment = [...equipmentList1, ...equipmentList2, ...equipmentList3];
  const item = allEquipment.find(item => item.id === itemId);
  
  if (!item) {
    debug.warn('talentCategories', `Equipment item not found: ${itemId}`);
    return null;
  }
  
  return item;
};

/**
 * SEARCH AND FILTER UTILITY FUNCTIONS
 */

/**
 * Search categories by name (case-insensitive).
 * @param {string} searchTerm - Search term.
 * @returns {string[]} Array of matching category names.
 */
export const searchCategories = (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') {
    return getCategories();
  }
  
  const term = searchTerm.toLowerCase().trim();
  return getCategories().filter(category => 
    category.toLowerCase().includes(term)
  );
};

/**
 * Search sub-categories by name within a category.
 * @param {string} talentCategory - The talent category.
 * @param {string} searchTerm - Search term.
 * @returns {string[]} Array of matching sub-category names.
 */
export const searchSubCategories = (talentCategory, searchTerm) => {
  const subCategories = getSubCategories(talentCategory);
  
  if (!searchTerm || typeof searchTerm !== 'string') {
    return subCategories;
  }
  
  const term = searchTerm.toLowerCase().trim();
  return subCategories.filter(subCategory => 
    subCategory.toLowerCase().includes(term)
  );
};

/**
 * Search style/genre options by name within a category and sub-category.
 * @param {string} talentCategory - The talent category.
 * @param {string} subCategory - The sub-category.
 * @param {string} searchTerm - Search term.
 * @returns {string[]} Array of matching style/genre names.
 */
export const searchStyleGenres = (talentCategory, subCategory, searchTerm) => {
  const styleGenres = getStyleGenreOptions(talentCategory, subCategory);
  
  if (!searchTerm || typeof searchTerm !== 'string') {
    return styleGenres;
  }
  
  const term = searchTerm.toLowerCase().trim();
  return styleGenres.filter(styleGenre => 
    styleGenre.toLowerCase().includes(term)
  );
};

/**
 * DATA EXPORT FUNCTIONS
 */

/**
 * Export the complete category structure for external use.
 * @returns {Object} Complete category structure.
 */
export const exportCategoryStructure = () => {
  return {
    categories: talentCategories,
    provinces: provinces,
    equipmentLists: {
      list1: equipmentList1,
      list2: equipmentList2,
      list3: equipmentList3
    },
    equipmentMapping: categoryToEquipmentMapping,
    metadata: {
      totalCategories: Object.keys(talentCategories).length,
      categoriesWithGenres: ['Musician & DJ'],
      categoriesWithEquipmentList1: ['Musician & DJ'],
      categoriesWithEquipmentList2: ['Comedy', 'MCs & Public Speaking', 'Dance & Rhythm'],
      categoriesWithEquipmentList3: ['Spectacle & Wonder', 'Fitness & Sports', 'Acting & Spoken Word', 'Imagery & Visual Arts'],
      maxStyleGenreSelections: getMaxStyleGenreSelections(),
      version: '2.0.0',
      lastUpdated: new Date().toISOString()
    }
  };
};

/**
 * VALIDATION FUNCTIONS
 */

/**
 * Comprehensive validation of talent profile data.
 * @param {Object} profileData - Talent profile data.
 * @returns {Object} Validation result with errors array.
 */
export const validateTalentProfile = (profileData) => {
  const errors = [];
  
  if (!profileData) {
    errors.push('Profile data is required');
    return { isValid: false, errors };
  }
  
  // Validate talent category
  if (!profileData.talentCategory) {
    errors.push('Talent category is required');
  } else if (!getCategories().includes(profileData.talentCategory)) {
    errors.push(`Invalid talent category: ${profileData.talentCategory}`);
  }
  
  // Validate performance type (sub-category)
  if (!profileData.performanceType) {
    errors.push('Performance type is required');
  } else if (profileData.talentCategory && !getPerformanceTypes(profileData.talentCategory).includes(profileData.performanceType)) {
    errors.push(`Invalid performance type for category ${profileData.talentCategory}: ${profileData.performanceType}`);
  }
  
  // UPDATED: Fixed validation for style array
  if (profileData.style && Array.isArray(profileData.style) && profileData.style.length > 0) {
    if (profileData.talentCategory === "Musician & DJ") {
      // For Musician & DJ, style should contain the performanceType (backward compatibility)
      const validPerformanceTypes = getPerformanceTypes(profileData.talentCategory);
      const invalidStyles = profileData.style.filter(style => !validPerformanceTypes.includes(style));
      if (invalidStyles.length > 0) {
        errors.push(`Invalid styles for Musician & DJ: ${invalidStyles.join(', ')}`);
      }
    } else {
      // For other categories, check if style contains performance type (backward compatibility)
      const validPerformanceTypes = getPerformanceTypes(profileData.talentCategory);
      const validStyles = getStyles(profileData.talentCategory, profileData.performanceType);
      
      // Allow either performance types (for backward compatibility) or actual styles
      const invalidStyles = profileData.style.filter(style => 
        !validPerformanceTypes.includes(style) && !validStyles.includes(style)
      );
      
      if (invalidStyles.length > 0) {
        errors.push(`Invalid styles: ${invalidStyles.join(', ')}`);
      }
    }
  }
  
  // Validate genres for "Musician & DJ" category
  if (profileData.talentCategory === "Musician & DJ" && profileData.genres) {
    if (!Array.isArray(profileData.genres)) {
      errors.push('Genres must be an array');
    } else if (profileData.genres.length === 0) {
      errors.push('At least one genre must be selected for Musician & DJ');
    } else if (profileData.genres.length > getMaxStyleGenreSelections()) {
      errors.push(`Maximum ${getMaxStyleGenreSelections()} genres allowed`);
    } else {
      const validGenres = getStyleGenreOptions(profileData.talentCategory, profileData.performanceType);
      const invalidGenres = profileData.genres.filter(genre => !validGenres.includes(genre));
      if (invalidGenres.length > 0) {
        errors.push(`Invalid genres: ${invalidGenres.join(', ')}`);
      }
    }
  }
  
  // Validate toggles
  if (profileData.interactive !== undefined && typeof profileData.interactive !== 'boolean') {
    errors.push('Interactive must be a boolean');
  }
  
  if (profileData.childEntertainment !== undefined && typeof profileData.childEntertainment !== 'boolean') {
    errors.push('Child entertainment must be a boolean');
  }
  
  debug.log('talentCategories', 'Profile validation completed', { 
    isValid: errors.length === 0, 
    errorCount: errors.length 
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};